/**
 * AR Grid Display V2 - JavaScript Module
 * Handles slider functionality, add-to-cart, and grid interactions
 */

// Prevent duplicate loading
if (typeof window.ARGridV2Loaded === 'undefined') {
  window.ARGridV2Loaded = true;

/**
 * ShopifySlider Class
 * Lightweight slider for logo grids and image carousels
 */
class ShopifySlider {
  constructor(selector, options = {}) {
    // Default options
    this.defaults = {
      slidesToShow: 1,
      slidesToScroll: 1,
      autoplay: false,
      autoplaySpeed: 3000,
      infinite: true,
      draggable: true,
      gap: 30,
      responsive: []
    };

    // Merge defaults with user options
    this.options = { ...this.defaults, ...options };

    // Find slider element
    this.slider = typeof selector === 'string'
      ? document.querySelector(selector)
      : selector;

    if (!this.slider) return;

    // Get slider elements
    this.track = this.slider.querySelector('.logo-slider__track');
    if (!this.track) return;

    this.slides = Array.from(this.track.children);
    if (!this.slides.length) return;

    // Set up properties
    this.currentSlide = 0;
    this.slideCount = this.slides.length;
    this.isDragging = false;
    this.startPos = 0;
    this.currentTranslate = 0;
    this.prevTranslate = 0;
    this.autoplayInterval = null;

    // Initialize slider
    this.init();
  }

  init() {
    // Set up slider styles
    this.slider.style.position = 'relative';
    this.slider.style.overflow = 'hidden';
    this.track.style.display = 'flex';
    this.track.style.gap = `${this.options.gap}px`;
    this.track.style.transition = 'transform 0.3s ease-in-out';

    // Calculate slide width
    this.updateSliderDimensions();

    // Add event listeners for dragging
    if (this.options.draggable) {
      this.setupDragEvents();
    }

    // Start autoplay
    if (this.options.autoplay) {
      this.startAutoplay();
    }

    // Handle resize
    window.addEventListener('resize', this.debounce(() => {
      this.updateSliderDimensions();
    }, 250));

    // Initial position
    this.goToSlide(0);
  }

  setupDragEvents() {
    this.track.addEventListener('mousedown', this.startDrag.bind(this));
    this.track.addEventListener('touchstart', this.startDrag.bind(this));
    this.track.addEventListener('mousemove', this.drag.bind(this));
    this.track.addEventListener('touchmove', this.drag.bind(this));
    this.track.addEventListener('mouseup', this.endDrag.bind(this));
    this.track.addEventListener('mouseleave', this.endDrag.bind(this));
    this.track.addEventListener('touchend', this.endDrag.bind(this));
    this.track.addEventListener('contextmenu', e => e.preventDefault());
  }

  startDrag(e) {
    if (this.options.autoplay) this.stopAutoplay();
    this.isDragging = true;
    this.startPos = this.getPositionX(e);
    this.track.style.cursor = 'grabbing';
  }

  drag(e) {
    if (!this.isDragging) return;
    const currentPosition = this.getPositionX(e);
    this.currentTranslate = this.prevTranslate + currentPosition - this.startPos;
  }

  endDrag() {
    if (!this.isDragging) return;
    this.isDragging = false;
    this.track.style.cursor = 'grab';

    const moveBy = this.currentTranslate - this.prevTranslate;
    if (Math.abs(moveBy) > 50) {
      if (moveBy < 0) {
        this.nextSlide();
      } else {
        this.prevSlide();
      }
    } else {
      this.goToSlide(this.currentSlide);
    }

    if (this.options.autoplay) this.startAutoplay();
  }

  getPositionX(e) {
    return e.type.includes('mouse') ? e.pageX : e.touches[0].clientX;
  }

  setSliderPosition() {
    this.track.style.transform = `translateX(${this.currentTranslate}px)`;
  }

  goToSlide(index) {
    // Handle infinite looping
    if (this.options.infinite) {
      if (index < 0) {
        index = this.slideCount - this.options.slidesToShow;
      } else if (index > this.slideCount - this.options.slidesToShow) {
        index = 0;
      }
    } else {
      // Clamp index
      index = Math.max(0, Math.min(index, this.slideCount - this.options.slidesToShow));
    }

    this.currentSlide = index;
    this.currentTranslate = -(index * (this.slideWidth + this.options.gap));
    this.prevTranslate = this.currentTranslate;

    this.track.style.transition = 'transform 0.3s ease-in-out';
    this.setSliderPosition();
  }

  prevSlide() {
    this.goToSlide(this.currentSlide - this.options.slidesToScroll);
  }

  nextSlide() {
    this.goToSlide(this.currentSlide + this.options.slidesToScroll);
  }

  startAutoplay() {
    this.autoplayInterval = setInterval(() => {
      this.nextSlide();
    }, this.options.autoplaySpeed);
  }

  stopAutoplay() {
    clearInterval(this.autoplayInterval);
  }

  updateSliderDimensions() {
    this.slideWidth = this.slider.offsetWidth / this.options.slidesToShow;
    this.slides.forEach(slide => {
      slide.style.minWidth = `${this.slideWidth}px`;
      slide.style.flexShrink = '0';
    });
    this.goToSlide(this.currentSlide);
  }

  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  destroy() {
    this.stopAutoplay();
    this.track.style.transform = '';
    this.track.style.transition = '';
  }
}

/**
 * Add to Cart Functionality
 * Handles adding products to cart from grid
 */
window.addToCartFromGrid = async function(variantId, productTitle) {
  try {
    // Add to cart via Shopify AJAX API
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        items: [{
          id: variantId,
          quantity: 1
        }]
      })
    });

    if (!response.ok) {
      throw new Error('Failed to add to cart');
    }

    const data = await response.json();

    // Update cart count if drawer exists
    if (window.theme && window.theme.cart) {
      window.theme.cart.refresh();
    }

    // Dispatch custom event for cart update
    document.dispatchEvent(new CustomEvent('cart:updated', {
      detail: { items: data }
    }));

    // Publish to theme's pub/sub system for cart drawer and abandoned cart recovery
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'ar-grid-v2', cartData: data });
    }

    // Show success notification
    showNotification(`${productTitle} added to cart!`, 'success');

    // Optional: Open cart drawer if it exists
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer && typeof cartDrawer.open === 'function') {
      cartDrawer.open();
    }

  } catch (error) {
    console.error('Error adding to cart:', error);
    showNotification('Unable to add to cart. Please try again.', 'error');
  }
};

/**
 * Quick View Modal (Optional)
 * Opens product quick view if modal system exists
 */
window.openQuickView = function(productHandle) {
  // Check if theme has quick view functionality
  if (window.theme && window.theme.quickView) {
    window.theme.quickView.open(productHandle);
  } else {
    // Fallback: Navigate to product page
    window.location.href = `/products/${productHandle}`;
  }
};

/**
 * Notification System
 * Shows toast-style notifications
 */
function showNotification(message, type = 'info') {
  // Check if theme has notification system
  if (window.theme && window.theme.notification) {
    window.theme.notification.show(message, type);
    return;
  }

  // Fallback: Create simple notification
  const notification = document.createElement('div');
  notification.className = `ar-notification ar-notification--${type}`;
  notification.textContent = message;
  notification.style.cssText = `
    position: fixed;
    bottom: 20px;
    right: 20px;
    background: ${type === 'success' ? '#4CAF50' : '#f44336'};
    color: white;
    padding: 15px 20px;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
    z-index: 10000;
    animation: slideInUp 0.3s ease;
    font-size: 1.4rem;
  `;

  document.body.appendChild(notification);

  setTimeout(() => {
    notification.style.animation = 'slideOutDown 0.3s ease';
    setTimeout(() => notification.remove(), 300);
  }, 3000);
}

/**
 * Initialize all sliders on page load
 */
document.addEventListener('DOMContentLoaded', () => {
  // Initialize logo sliders
  const logoSliders = document.querySelectorAll('.logo-slider');
  logoSliders.forEach(slider => {
    const track = slider.querySelector('.logo-slider__track');
    if (!track) return;

    const autoplay = track.dataset.autoplay === 'true';
    const autoplaySpeed = parseInt(track.dataset.autoplaySpeed) || 10000;

    new ShopifySlider(slider, {
      slidesToShow: 5,
      slidesToScroll: 1,
      autoplay: autoplay,
      autoplaySpeed: autoplaySpeed,
      infinite: true,
      draggable: true,
      gap: 30,
      responsive: [
        {
          breakpoint: 1200,
          settings: {
            slidesToShow: 4
          }
        },
        {
          breakpoint: 989,
          settings: {
            slidesToShow: 3
          }
        },
        {
          breakpoint: 749,
          settings: {
            slidesToShow: 2
          }
        }
      ]
    });
  });
});

/**
 * Simple animations for notifications
 */
if (!document.querySelector('#ar-grid-animations')) {
  const style = document.createElement('style');
  style.id = 'ar-grid-animations';
  style.textContent = `
    @keyframes slideInUp {
      from {
        transform: translateY(100%);
        opacity: 0;
      }
      to {
        transform: translateY(0);
        opacity: 1;
      }
    }
    @keyframes slideOutDown {
      from {
        transform: translateY(0);
        opacity: 1;
      }
      to {
        transform: translateY(100%);
        opacity: 0;
      }
    }
  `;
  document.head.appendChild(style);
}

// Export for use in other modules if needed (safe for both browser and module contexts)
if (typeof module !== 'undefined' && module.exports) {
  module.exports = { ShopifySlider, showNotification };
} else if (typeof window !== 'undefined') {
  window.ShopifySlider = ShopifySlider;
  window.showNotification = showNotification;
}

} // End of duplicate prevention check
